import { selectProfile } from '@src/store/profile/profileSlice';
import { Collapse, Typography } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const { Title } = Typography;
const { Panel } = Collapse;

interface PermissionSectionProps {
    permissions: any[];
    renderPermissionItem: (
        permission: any,
        isChild?: boolean,
        isHeader?: boolean,
        parentCheckboxStyle?: 'check' | 'dash'
    ) => ReactNode;
}

export default function PermissionSection({
    permissions,
    renderPermissionItem,
}: PermissionSectionProps) {
    const { t } = useTranslation();
    const profile = useSelector(selectProfile);
    return (
        <div className="!mt-0">
            <Title level={4} className="mb-6">
                {t('pages.role.form.permissions.title')}
            </Title>

            <style>
                {`
            .ant-collapse > .ant-collapse-item > .ant-collapse-header {
                align-items: center !important;
                display: flex !important;
                background: none !important;
                padding: 0 !important;
            }
            .ant-collapse-arrow {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
            }
            .ant-collapse > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
                padding: 0 !important;
            }
            .ant-collapse > .ant-collapse-item {
                border-radius: 8px !important;
                margin-bottom: 16px !important;
                border: 1px solid #e5e7eb !important;
            }
        `}
            </style>

            <Collapse bordered={false} size="large">
                {/* Dashboard Panel */}
                <Panel
                    className={`border rounded-lg ${
                        permissions.find((item) => item.key === 'Dashboard')
                            ?.access !== 'none'
                            ? 'bg-infoContainerLight'
                            : 'bg-background'
                    }`}
                    header={renderPermissionItem(
                        permissions.find((p) => p.key === 'Dashboard'),
                        false,
                        true
                    )}
                    key="Dashboard"
                    collapsible="icon"
                    showArrow={false}
                />
                {/* SIMs Panel */}
                <Panel
                    className={`border rounded-lg ${
                        permissions.find((item) => item.key === 'Sims')
                            ?.access !== 'none'
                            ? 'bg-infoContainerLight'
                            : 'bg-background'
                    }`}
                    header={renderPermissionItem(
                        permissions.find((p) => p.key === 'Sims'),
                        false,
                        true
                    )}
                    key="Sims"
                    collapsible="icon"
                    showArrow={false}
                />
                {/* Plan Management Panel */}
                <Panel
                    className={`border rounded-lg ${
                        permissions.find(
                            (item) => item.key === 'Plan_Management'
                        )?.access !== 'none'
                            ? 'bg-infoContainerLight'
                            : 'bg-background'
                    }`}
                    header={renderPermissionItem(
                        permissions.find((p) => p.key === 'Plan_Management'),
                        false,
                        true,
                        'dash'
                    )}
                    key="Plan_Management"
                >
                    {permissions
                        .find((p) => p.key === 'Plan_Management')
                        ?.children.map((child) =>
                            renderPermissionItem(child, true)
                        )}
                </Panel>
                {/* Sub-Accounts Panel */}
                <Panel
                    className={`border rounded-lg ${
                        permissions.find((item) => item.key === 'SubAccounts')
                            ?.access !== 'none'
                            ? 'bg-infoContainerLight'
                            : 'bg-background'
                    }`}
                    header={renderPermissionItem(
                        permissions.find((p) => p.key === 'SubAccounts'),
                        false,
                        true
                    )}
                    key="SubAccounts"
                    collapsible="icon"
                    showArrow={false}
                />
                {/* User Management Panel */}
                <Panel
                    className={`border rounded-lg ${
                        permissions.find(
                            (item) => item.key === 'Parent-UserManagement'
                        )?.access !== 'none'
                            ? 'bg-infoContainerLight'
                            : 'bg-background'
                    }`}
                    header={renderPermissionItem(
                        permissions.find(
                            (p) => p.key === 'Parent-UserManagement'
                        ),
                        false,
                        true,
                        'dash'
                    )}
                    key="Parent-UserManagement"
                >
                    {permissions
                        .find((p) => p.key === 'Parent-UserManagement')
                        ?.children.map((child) =>
                            renderPermissionItem(child, true)
                        )}
                </Panel>
                {/* B2C Management Panel */}
                {profile?.isHost && (
                    <Panel
                        className={`border rounded-lg ${
                            permissions.find(
                                (item) => item.key === 'B2CManagement'
                            )?.access !== 'none'
                                ? 'bg-infoContainerLight'
                                : 'bg-background'
                        }`}
                        header={renderPermissionItem(
                            permissions.find((p) => p.key === 'B2CManagement'),
                            false,
                            true,
                            'dash'
                        )}
                        key="B2CManagement"
                    >
                        {permissions
                            .find((p) => p.key === 'B2CManagement')
                            ?.children.map((child) =>
                                renderPermissionItem(child, true)
                            )}
                    </Panel>
                )}
            </Collapse>
        </div>
    );
}
